import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";

/**
 * PermissionsLoader - Full-screen component shown during root-level user permission bootstrap
 *
 * Has two modes:
 * 1. **Loading** (default): Shows an animated loading indicator while permissions are
 *    being fetched. After 10 seconds, shows a "Taking longer than expected?" escape
 *    hatch with a "Re-authenticate" link.
 * 2. **Error** (when `error` prop is set): Shows a "Server Unavailable" message with
 *    a "Re-authenticate" link. This prevents the infinite-loop that would occur if
 *    we redirected to "/" on failure — MSAL still has the user logged in, so the
 *    redirect would re-trigger the same failing API call endlessly.
 *
 * In both cases, "Re-authenticate" clears the MSAL cache before redirecting to "/",
 * ensuring the user lands on the login page instead of looping.
 *
 * NOTE: This component renders BEFORE the main app layout and cannot rely on Bootstrap
 * CSS or app stylesheets being fully loaded. All styles are inlined to ensure correct
 * display during the critical bootstrap phase, regardless of CSS loading state.
 *
 * Uses Bootstrap CSS variables (--bs-body-bg, --bs-body-color) for theme-awareness,
 * but all layout and animation styles are self-contained.
 */
export const PermissionsLoader: React.FC<{
  error?: string | null;
}> = ({ error = null }) => {
  const { instance } = useMsal();
  const [showEscapeHatch, setShowEscapeHatch] = useState(false);

  // Show escape hatch after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowEscapeHatch(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Handle logout - clear MSAL cache and redirect to home
  const handleLogout = () => {
    instance.setActiveAccount(null);
    instance.clearCache();
    window.location.href = "/";
  };

  // ---------- Error state ----------
  // Shows "Server Unavailable" with a "Re-authenticate" link, matching the
  // visual format of the loading state's escape hatch for a consistent UX.
  // Re-authenticate clears the MSAL cache first, preventing the infinite loop.
  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100 permissions-loader-bg">
        <style>{`
          .permissions-loader-bg {
            background-color: var(--bs-body-bg);
            color: var(--bs-body-color);
          }
          .permissions-escape-link {
            color: var(--bs-primary, #0d6efd);
            text-decoration: underline;
            cursor: pointer;
            background: none;
            border: none;
            font-size: inherit;
            padding: 0;
          }
          .permissions-escape-link:hover {
            color: var(--bs-primary, #0d6efd);
            text-decoration: none;
          }
        `}</style>
        <div className="text-center">
          <h1>Service Unavailable</h1>
          <p style={{ fontSize: "0.9rem", opacity: 0.7 }}>
            Please try again later.
          </p>
          <button className="permissions-escape-link" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>
    );
  }

  // ---------- Loading state ----------
  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 permissions-loader-bg">
      <style>{`
        .permissions-loader-bg {
          background-color: var(--bs-body-bg);
          color: var(--bs-body-color);
          animation: permissions-fade-out 0.35s ease-out forwards;
          backdrop-filter: blur(10px);
        }
        @keyframes permissions-fade-out {
          from {
            opacity: 1;
            backdrop-filter: blur(10px);
          }
        }
        .permissions-loader-dots {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        .permissions-loader-dot {
          width: 0.75rem;
          height: 0.75rem;
          border-radius: 50%;
          background-color: var(--bs-body-color);
          animation: permissions-loader-pulse 1.4s infinite;
        }
        .permissions-loader-dot:nth-child(1) {
          animation-delay: 0s;
        }
        .permissions-loader-dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .permissions-loader-dot:nth-child(3) {
          animation-delay: 0.4s;
        }
        
        .permissions-loading-text {
          position: relative;
        }
        .permissions-animated-dots {
          position: absolute;
        }
        .permissions-animated-dots::after {
          content: '...';
          animation: permissions-dots-animation 1.5s steps(4, end) infinite;
          display: inline-block;
          width: 1.5em;
          text-align: left;
        }
        @keyframes permissions-dots-animation {
          0% { content: ''; }
          25% { content: '.'; }
          50% { content: '..'; }
          75% { content: '...'; }
        }
        .permissions-escape-hatch {
          opacity: 0;
          animation: permissions-fade-in 0.5s ease-out forwards;
        }
        @keyframes permissions-fade-in {
          to { opacity: 1; }
        }
        .permissions-escape-link {
          color: var(--bs-primary, #0d6efd);
          text-decoration: underline;
          cursor: pointer;
          background: none;
          border: none;
          font-size: inherit;
          padding: 0;
        }
        .permissions-escape-link:hover {
          color: var(--bs-primary, #0d6efd);
          text-decoration: none;
        }
      `}</style>
      <div className="text-center">
        <div className="permissions-loader-dots mb-3">
          <div className="permissions-loader-dot"></div>
          <div className="permissions-loader-dot"></div>
          <div className="permissions-loader-dot"></div>
        </div>
        <h1>
          <span className="permissions-loading-text">Loading</span>
          {!showEscapeHatch && (
            <span className="permissions-animated-dots"></span>
          )}
        </h1>
        {showEscapeHatch && (
          <div className="permissions-escape-hatch mt-3">
            <p style={{ fontSize: "0.9rem", opacity: 0.7 }}>
              Taking longer than expected?
            </p>
            <button className="permissions-escape-link" onClick={handleLogout}>
              Re-authenticate
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
